import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faHistory, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'
import { useAccountContext } from '../../contexts'
import { api } from '../../services'

const ALERT_TYPES = [
  'SENSOR_ALERTS',
  'BATTERY_LOW',
  'DEVICE_OFFLINE',
  'SYSTEM_CONTROL_MESSAGES',
  'TEST_MESSAGES',
]

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800',
  WARNING: 'bg-amber-100 text-amber-800',
  INFO: 'bg-blue-100 text-blue-800',
}

const PAGE_SIZE = 50

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

export function AlertHistoryTab() {
  const { devices } = useAccountContext()
  const [alerts, setAlerts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [alertTypeFilter, setAlertTypeFilter] = useState('')
  const [deviceFilter, setDeviceFilter] = useState<number | undefined>(undefined)

  const devicesArray = Object.values(devices)

  useEffect(() => {
    fetchAlerts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, alertTypeFilter, deviceFilter])

  async function fetchAlerts() {
    setLoading(true)
    try {
      const params: { limit: number; offset: number; alertType?: string; deviceId?: number } = {
        limit: PAGE_SIZE,
        offset,
      }
      if (alertTypeFilter) params.alertType = alertTypeFilter
      if (deviceFilter) params.deviceId = deviceFilter
      const response = await api.getAlertHistory(params)
      setAlerts(response?.data || [])
      setTotal(response?.pagination?.total || 0)
    } catch (error) {
      console.error('Failed to fetch alert history', error)
      setAlerts([])
    }
    setLoading(false)
  }

  function handleFilterChange() {
    setOffset(0)
  }

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const totalPages = Math.ceil(total / PAGE_SIZE)

  function getDeviceName(deviceId: number): string {
    const device = (devices as any)[deviceId]
    return device?.name || `Device ${deviceId}`
  }

  return (
    <div className="p-6 space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-end space-y-4 sm:space-y-0 sm:space-x-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Alert Type</label>
          <select
            value={alertTypeFilter}
            onChange={(e) => {
              setAlertTypeFilter(e.target.value)
              handleFilterChange()
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
          >
            <option value="">All Types</option>
            {ALERT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Device</label>
          <select
            value={deviceFilter || ''}
            onChange={(e) => {
              setDeviceFilter(e.target.value ? Number(e.target.value) : undefined)
              handleFilterChange()
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
          >
            <option value="">All Devices</option>
            {devicesArray.map((device: any) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.deviceName || `Device ${device.deviceId}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-8">
          <FontAwesomeIcon icon={faHistory} className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Alert History</h3>
          <p className="text-gray-600">
            No historical alerts match the current filters.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recovery?
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {alerts.map((alert: any, index: number) => (
                  <tr key={alert.id || index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {alert.createdAt ? formatDate(alert.createdAt) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {alert.deviceId ? getDeviceName(alert.deviceId) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {alert.alertType?.replace(/_/g, ' ') || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {alert.severity ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[alert.severity] || 'bg-gray-100 text-gray-800'}`}
                        >
                          {alert.severity}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                      {alert.message || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {alert.isRecovery ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5 text-green-500" />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">{offset + 1}</span>
              {' '}-{' '}
              <span className="font-medium">{Math.min(offset + PAGE_SIZE, total)}</span>
              {' '}of{' '}
              <span className="font-medium">{total}</span>
              {' '}results
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4 mr-1" />
                Previous
              </button>
              <span className="inline-flex items-center px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
